// The app's record of the signed-in session, cached in localStorage by AuthProvider. Written on
// every sign-in and removed whenever the app ends the session, so it is the app's own account of
// who is signed in — independent of the provider token stores, which their SDKs can empty
// behind the app's back.

export interface User {
  name: string;
  email: string;
  provider: 'okta' | 'microsoft' | null;
}

export interface CachedAuthState {
  user: User | null;
  isAuthenticated: boolean;
}

export const AUTH_STATE_KEY = 'authState';

export function readCachedAuthState(): CachedAuthState | null {
  try {
    const cached = localStorage.getItem(AUTH_STATE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

// The provider of the session the app currently records as signed in, or null if none. Lets
// getV2AuthHeaders tell a session whose tokens were discarded from a visitor who never had one.
export function cachedSessionProvider(): User['provider'] {
  const cached = readCachedAuthState();
  return cached?.isAuthenticated ? cached.user?.provider ?? null : null;
}

// A native Microsoft session is NOT described here. It used to be, by reading the implicit flow's
// access token out of localStorage — but that flow was replaced by authorization code + PKCE, whose
// session lives in the platform keystore and, crucially, CAN authenticate a v2 call. Ask
// services/nativeMsAuth instead: hasLiveNativeMicrosoftSession for a session that can authenticate
// now, hasStoredNativeMicrosoftSession for one that exists but needs re-authenticating.
//
// Deliberately not left here as a fallback. Reading the old key would report a session that no
// longer lives there, and both of its historical callers used the answer to skip recovery —
// reintroducing the state where the app renders as signed in while every API call fails.
